import {
    DateFormats,
    formatDate,
    formatGender,
    formatNameFirstLast,
    formatPhoneNumber,
    getAge,
} from '@strata/helper-functions';
import { InfoDialog } from '@strata/react-dialog';
import { EnrollmentCoverageDates } from '@strata/react-enrollment-dates';
import { MailToHyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import {
    AddressDetailLabelValue,
    DetailLabelValuePair,
    FormTitleLabel,
} from '@strata/react-label-value-pairs';
import { Spinner } from '@strata/react-loading-animation';
import PropTypes from 'prop-types';

import { EnrollmentStatusIndicator } from 'components/enrollment/EnrollmentStatusIndicator';
import { EnrollmentStatus } from 'services/memberHelpers';

const memberDetailsShape = {
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    gender: PropTypes.string,
    dateOfBirth: PropTypes.string,
    memberAddress1: PropTypes.string,
    memberAddress2: PropTypes.string,
    memberCity: PropTypes.string,
    memberState: PropTypes.string,
    memberZip: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    enrollmentStatus: PropTypes.string,
    enrollmentSource: PropTypes.string,
    enrollmentCheckDate: PropTypes.string,
    isFmhEnrollmentEnd: PropTypes.bool,
    enrollmentStart: PropTypes.string,
    enrollmentEnd: PropTypes.string,
    willBeUnmanagedOn: PropTypes.string,
    account: PropTypes.string,
    lineOfBusiness: PropTypes.string,
    relationshipCode: PropTypes.string,
    relationshipCodeDetailed: PropTypes.string,
    isAso: PropTypes.string,
    carrier: PropTypes.string,
    benefitGroup: PropTypes.string,
    pharmacyCoverage: PropTypes.string,
    isMarketplaceEligible: PropTypes.string,
};

const memberDetailsPropType = PropTypes.shape(memberDetailsShape);

const EnrollmentStatusSection = ({ memberDetails = undefined, isMonitoring }) => (
    <div className="flex__gap--large margin__bottom--medium">
        <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
            <DetailLabelValuePair
                label="Enrollment status"
                value={<EnrollmentStatusIndicator status={memberDetails?.enrollmentStatus} />}
            />
            <DetailLabelValuePair
                label="Enrollment source"
                value={memberDetails?.enrollmentSource}
            />
        </div>
        <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
            <DetailLabelValuePair
                label="Enrollment check date"
                value={formatDate(memberDetails?.enrollmentCheckDate, DateFormats.DATE_TIME)}
            />
            {isMonitoring ? (
                <DetailLabelValuePair
                    label="Grace period ends"
                    value={formatDate(memberDetails?.willBeUnmanagedOn)}
                />
            ) : (
                <DetailLabelValuePair
                    label="Coverage dates"
                    value={
                        <EnrollmentCoverageDates
                            enrollmentStart={memberDetails?.enrollmentStart}
                            enrollmentEnd={memberDetails?.enrollmentEnd}
                            isFmhEnrollmentEnd={memberDetails?.isFmhEnrollmentEnd}
                        />
                    }
                />
            )}
        </div>
    </div>
);

EnrollmentStatusSection.propTypes = {
    memberDetails: memberDetailsPropType,
    isMonitoring: PropTypes.bool.isRequired,
};

const EnrollmentPatientSection = ({ memberDetails = undefined, dateOfBirth = undefined }) => (
    <>
        <div className="margin__top--large margin__bottom--small">
            <FormTitleLabel label="Patient" />
        </div>
        <div className="flex__gap--large margin__bottom--medium">
            <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
                <DetailLabelValuePair
                    label="Name"
                    value={formatNameFirstLast(memberDetails?.firstName, memberDetails?.lastName)}
                />
                <DetailLabelValuePair label="Date of birth" value={formatDate(dateOfBirth)} />
                <AddressDetailLabelValue
                    label="Address"
                    className="padding__bottom--small"
                    address1={memberDetails?.memberAddress1}
                    address2={memberDetails?.memberAddress2}
                    city={memberDetails?.memberCity}
                    state={memberDetails?.memberState}
                    zip={memberDetails?.memberZip}
                />
                <DetailLabelValuePair
                    label="Email"
                    value={<MailToHyperlink emailAddress={memberDetails?.email} />}
                />
            </div>
            <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
                <DetailLabelValuePair label="Gender" value={formatGender(memberDetails?.gender)} />
                <DetailLabelValuePair label="Age" value={getAge(dateOfBirth)} />
                <DetailLabelValuePair
                    label="Phone"
                    value={formatPhoneNumber(memberDetails?.phone)}
                />
            </div>
        </div>
    </>
);

EnrollmentPatientSection.propTypes = {
    memberDetails: memberDetailsPropType,
    dateOfBirth: PropTypes.string,
};

const EnrollmentHealthPlanSection = ({ memberDetails = undefined, memberId = undefined }) => (
    <>
        <div className="margin__top--large margin__bottom--small">
            <FormTitleLabel label="Health Plan" />
        </div>
        <div className="flex__gap--large margin__bottom--medium">
            <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
                <DetailLabelValuePair label="Member ID" value={memberId} />
                <DetailLabelValuePair label="Account" value={memberDetails?.account} />
                <DetailLabelValuePair
                    label="Line of business"
                    value={memberDetails?.lineOfBusiness}
                />
                <DetailLabelValuePair
                    label="Relationship code"
                    value={memberDetails?.relationshipCodeDetailed}
                />
                <DetailLabelValuePair
                    label="Marketplace"
                    value={memberDetails?.isMarketplaceEligible}
                />
            </div>
            <div className="flex__gap--medium" style={{ flexDirection: 'column', width: '50%' }}>
                <DetailLabelValuePair label="Carrier" value={memberDetails?.carrier} />
                <DetailLabelValuePair label="Group ID" value={memberDetails?.benefitGroup} />
                <DetailLabelValuePair
                    label="Pharmacy coverage"
                    value={memberDetails?.pharmacyCoverage}
                />
                <DetailLabelValuePair
                    style={{ width: '50%' }}
                    label="Member Pin"
                    value={memberDetails?.memberPin}
                />
                <DetailLabelValuePair label="ASO" value={memberDetails?.isAso} />
            </div>
        </div>
    </>
);

EnrollmentHealthPlanSection.propTypes = {
    memberDetails: memberDetailsPropType,
    memberId: PropTypes.string,
};

const EnrollmentCheckContent = ({ memberDetails = undefined, memberId = undefined }) => {
    const isMonitoring = memberDetails?.enrollmentStatus === EnrollmentStatus.PENDING;

    return (
        <>
            <EnrollmentStatusSection memberDetails={memberDetails} isMonitoring={isMonitoring} />
            <EnrollmentPatientSection
                memberDetails={memberDetails}
                dateOfBirth={memberDetails?.dateOfBirth}
            />
            <EnrollmentHealthPlanSection memberDetails={memberDetails} memberId={memberId} />
        </>
    );
};

EnrollmentCheckContent.propTypes = {
    memberDetails: memberDetailsPropType,
    memberId: PropTypes.string,
};

const EnrollmentCheckLoadingBody = () => (
    <div
        className="flex__gap--medium padding__around--large"
        style={{ alignItems: 'center', justifyContent: 'center', minHeight: 220 }}
    >
        <Spinner className="padding__around--medium" />
    </div>
);

const EnrollmentCheckErrorBody = ({ errorMessage }) => {
    const isMissingFields = errorMessage.includes(
        'Missing member or plan information for benefit check',
    );
    return (
        <InlineMessage
            intent={isMissingFields ? InlineMessage.Intent.WARNING : InlineMessage.Intent.ERROR}
            label={errorMessage}
        />
    );
};

EnrollmentCheckErrorBody.propTypes = {
    errorMessage: PropTypes.string.isRequired,
};

export const EnrollmentCheckDialog = ({
    isOpen,
    onClose,
    memberId = undefined,
    memberDetails = undefined,
    isLoading = false,
    errorMessage = undefined,
}) => {
    const renderBody = () => {
        if (isLoading) {
            return <EnrollmentCheckLoadingBody />;
        }
        if (errorMessage) {
            return <EnrollmentCheckErrorBody errorMessage={errorMessage} />;
        }
        if (memberDetails) {
            return <EnrollmentCheckContent memberDetails={memberDetails} memberId={memberId} />;
        }
        return null;
    };

    return (
        <InfoDialog
            dialogTitle="ENROLLMENT CHANGE HISTORY"
            width={600}
            isOpen={isOpen}
            onClose={onClose}
            dialogContent={renderBody()}
        />
    );
};

EnrollmentCheckDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    memberId: PropTypes.string,
    memberDetails: memberDetailsPropType,
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
};
